import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { countryLabel, findCountry, searchCountries, type Country } from '../lib/countries.js';

import { DISABLED_FIELD, NO_AUTOFILL } from './ui.js';

export interface CountrySelectProps {
  label: string;
  hint?: string;
  /** The ISO 3166-1 alpha-2 code currently chosen. */
  value: string;
  onChange: (code: string) => void;
  error?: string | undefined;
  /** Name of the hidden field carrying the code, if the form needs one. */
  name?: string;
  /**
   * Read-only, for a form the server would refuse - WAIT-B round 2.
   *
   * **The listbox has to be stopped as well as the input.** A disabled input
   * fires no focus, change or click event, but the three handlers that open the
   * list are not the only way in: `handleKeyDown` opens it on an arrow key, and
   * a list that opened over a disabled field would offer options that cannot be
   * chosen. So this returns early everywhere rather than only greying the box.
   */
  disabled?: boolean;
}

/**
 * A searchable country field that stores an ISO 3166-1 alpha-2 code.
 *
 * It replaces a two-character free-text box: same stored value, same database
 * constraint, but nobody has to know that Austria is AT and Australia is AU.
 *
 * Built here rather than pulled in as a dependency — it is one listbox, and a
 * select component is not worth a package and its transitive tree.
 */
export function CountrySelect({
  label,
  hint,
  value,
  onChange,
  error,
  name,
  disabled = false,
}: CountrySelectProps): React.JSX.Element {
  const inputId = useId();
  const listId = `${inputId}-list`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const selected = findCountry(value);
  const displayValue = selected ? countryLabel(selected) : value;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => (open ? searchCountries(query) : []), [open, query]);

  // Keep the highlight inside the list as it narrows.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // A click anywhere else abandons the search and restores what was chosen.
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) close();
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  function close(): void {
    setOpen(false);
    setQuery('');
  }

  function choose(country: Country): void {
    onChange(country.code);
    close();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (matches.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => (current + step + matches.length) % matches.length);
      return;
    }

    if (event.key === 'Enter') {
      if (!open) return;
      // Never let choosing a country submit the form as a side effect.
      event.preventDefault();
      const country = matches[activeIndex];
      if (country) choose(country);
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  }

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const activeId = open && matches[activeIndex] ? `${listId}-${matches[activeIndex]?.code}` : undefined;

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="mt-1 text-sm text-fg-muted">
          {hint}
        </p>
      ) : null}

      <input
        {...NO_AUTOFILL}
        id={inputId}
        role="combobox"
        type="text"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...(activeId ? { 'aria-activedescendant': activeId } : {})}
        disabled={disabled}
        value={open ? query : displayValue}
        onChange={(event) => {
          if (disabled) return;
          setOpen(true);
          setQuery(event.target.value);
        }}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
          setQuery('');
        }}
        // Reopens after a choice, when the field already has focus and no
        // focus event will fire.
        onClick={() => {
          if (!disabled && !open) {
            setOpen(true);
            setQuery('');
          }
        }}
        onKeyDown={handleKeyDown}
        className={`mt-2 block w-full rounded-control border-0 px-3 py-2 text-fg shadow-sm ring-1 ring-inset placeholder:text-fg-faint focus:ring-2 focus:ring-inset focus:ring-blue-ink sm:text-sm ${DISABLED_FIELD} ${
          error ? 'ring-danger' : 'ring-rule-strong'
        }`}
        placeholder={open ? 'Search countries…' : undefined}
      />

      {/* The value the form actually carries is always the code. */}
      {name ? <input type="hidden" name={name} value={value} /> : null}

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          // Keeps focus on the input, so choosing with the mouse does not blur
          // the field and unmount the option mid-click.
          onMouseDown={(event) => event.preventDefault()}
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-box bg-card py-1 text-sm shadow-lg ring-1 ring-rule"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-fg-muted">No country matches that.</li>
          ) : (
            matches.map((country, index) => (
              <li
                key={country.code}
                id={`${listId}-${country.code}`}
                role="option"
                aria-selected={country.code === value}
                onClick={() => choose(country)}
                onMouseEnter={() => setActiveIndex(index)}
                className={`cursor-pointer px-3 py-2 ${
                  index === activeIndex ? 'bg-sunken text-fg' : 'text-fg-2'
                }`}
              >
                {country.name} <span className="text-fg-muted">({country.code})</span>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
